import React from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';
import Slider from '@mui/material/Slider';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Link } from 'react-router-dom';
import { api, getApiErrorMessage } from '../api';
import { getSettings, saveSettings } from '../settings';
import StorageUsageBar, { formatBytes } from './StorageUsageBar';
import Avatar from '@mui/material/Avatar';
import { cacheProfile, getCachedProfile } from '../profileCache';

const buttonSx = { backgroundColor: '#222', color: '#fff', fontWeight: 'bold', '&:hover': { backgroundColor: '#333' } };

async function prepareAvatar(file) {
  const sourceUrl = URL.createObjectURL(file);
  try {
    const image = await new Promise((resolve, reject) => { const value = new Image(); value.onload = () => resolve(value); value.onerror = reject; value.src = sourceUrl; });
    const edge = Math.min(512, Math.max(image.width, image.height));
    const canvas = document.createElement('canvas'); canvas.width = edge; canvas.height = edge;
    const context = canvas.getContext('2d'); context.fillStyle = '#eeeeee'; context.fillRect(0, 0, edge, edge);
    const scale = Math.max(edge / image.width, edge / image.height); const width = image.width * scale; const height = image.height * scale;
    context.drawImage(image, (edge - width) / 2, (edge - height) / 2, width, height);
    return await new Promise(resolve => canvas.toBlob(resolve, 'image/webp', 0.76));
  } finally { URL.revokeObjectURL(sourceUrl); }
}

function Settings() {
  const [settings, setSettings] = React.useState(getSettings);
  const [passwords, setPasswords] = React.useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordMessage, setPasswordMessage] = React.useState({ type: '', text: '' });
  const [changingPassword, setChangingPassword] = React.useState(false);
  const [usage, setUsage] = React.useState(null);
  const [adminUsers, setAdminUsers] = React.useState(null);
  const [adminSearch, setAdminSearch] = React.useState('');
  const [adminLoading, setAdminLoading] = React.useState(false);
  const [profile, setProfile] = React.useState(getCachedProfile);
  const [avatarLoading, setAvatarLoading] = React.useState(false);

  React.useEffect(() => {
    api.get('/v1/storage/usage').then(({ data }) => setUsage(data)).catch(() => {});
    api.get('/v1/admin/users?limit=50').then(({ data }) => setAdminUsers(data.items || [])).catch(() => {});
    const cachedProfile = getCachedProfile();
    if (!cachedProfile) api.get('/v1/profile/me').then(({ data }) => setProfile(cacheProfile(data))).catch(() => {});
  }, []);

  const updateProfile = async changes => {
    try { const { data } = await api.put('/v1/profile/me/settings', { profileVisibility: profile.profileVisibility, myDexVisibility: profile.myDexVisibility, palsListVisibility: profile.palsListVisibility, showInPalsLists: profile.showInPalsLists, ...changes }); setProfile(cacheProfile(data)); }
    catch (error) { setPasswordMessage({ type: 'error', text: getApiErrorMessage(error, 'Unable to update privacy settings.') }); }
  };

  const uploadAvatar = async event => {
    const file = event.target.files?.[0]; event.target.value = '';
    if (!file) return;
    setAvatarLoading(true);
    try {
      const blob = await prepareAvatar(file);
      if (!blob) throw new Error('Unable to process avatar.');
      const { data: upload } = await api.post('/v1/profile/avatar/upload-url', { byteSize: blob.size });
      await fetch(upload.uploadUrl, { method: 'PUT', headers: { 'Content-Type': 'image/webp' }, body: blob });
      const { data } = await api.post('/v1/profile/avatar/complete', { objectKey: upload.objectKey });
      setProfile(cacheProfile(data));
    } catch (error) { setPasswordMessage({ type: 'error', text: getApiErrorMessage(error, 'Unable to upload avatar.') }); }
    finally { setAvatarLoading(false); }
  };

  const searchAdminUsers = async event => {
    event.preventDefault();
    setAdminLoading(true);
    try {
      const { data } = await api.get('/v1/admin/users', { params: { search: adminSearch, limit: 50 } });
      setAdminUsers(data.items || []);
    } catch (err) {
      setPasswordMessage({ type: 'error', text: getApiErrorMessage(err, 'Unable to load admin users.') });
    } finally {
      setAdminLoading(false);
    }
  };

  const update = changes => setSettings(saveSettings(changes));
  const updatePassword = field => event => setPasswords(current => ({ ...current, [field]: event.target.value }));
  const changePassword = async event => {
    event.preventDefault();
    if (passwords.newPassword !== passwords.confirmPassword) {
      setPasswordMessage({ type: 'error', text: 'New passwords do not match.' });
      return;
    }
    setChangingPassword(true);
    setPasswordMessage({ type: '', text: '' });
    try {
      await api.post('/v1/auth/change-password', { currentPassword: passwords.currentPassword, newPassword: passwords.newPassword });
      setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordMessage({ type: 'success', text: 'Password changed successfully.' });
    } catch (err) {
      setPasswordMessage({ type: 'error', text: getApiErrorMessage(err, 'Unable to change password.') });
    } finally {
      setChangingPassword(false);
    }
  };

  return <main className="page-shell">
    <Button component={Link} to="/" startIcon={<ArrowBackIcon />} sx={{ color: '#222', fontSize: '1rem', fontWeight: 'normal', textTransform: 'none', textDecoration: 'underline', p: 0, mb: 2, '&:hover': { color: '#000', backgroundColor: 'transparent', textDecoration: 'underline' } }}>Back</Button>
    <Typography variant="h4" sx={{ mb: 3 }}>Settings</Typography>
    {profile && <Card sx={{ maxWidth: 680, mb: 3 }}><CardContent sx={{ display: 'grid', gap: 2 }}>
      <Typography variant="h6" fontWeight="bold">Profile</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}><Avatar src={profile.avatarUrl || undefined} sx={{ width: 88, height: 88, fontSize: '2.5rem', bgcolor: '#777' }}>{profile.username?.[0]?.toUpperCase()}</Avatar><Box><Typography fontWeight="bold">{profile.username}</Typography><Button component="label" variant="contained" disabled={avatarLoading} sx={{ ...buttonSx, mt: 1 }}>{avatarLoading ? 'Uploading…' : 'Choose Avatar'}<input hidden type="file" accept="image/*" onChange={uploadAvatar} /></Button></Box></Box>
      <Typography variant="subtitle1" fontWeight="bold">Profile visibility</Typography>
      <TextField select label="Who can view your profile" value={profile.profileVisibility} onChange={event => updateProfile({ profileVisibility: event.target.value })} SelectProps={{ native: true }}><option value="Private">Private</option><option value="Friends">Digipals</option><option value="Public">Public</option></TextField>
      <TextField select label="Who can view your Dex" value={profile.myDexVisibility} onChange={event => updateProfile({ myDexVisibility: event.target.value })} SelectProps={{ native: true }}><option value="Private">Private</option><option value="Friends">Digipals</option><option value="Public">Public</option></TextField>
      <TextField select label="Who can view your Digipals list" value={profile.palsListVisibility} onChange={event => updateProfile({ palsListVisibility: event.target.value })} SelectProps={{ native: true }}><option value="Private">Private</option><option value="Friends">Digipals</option><option value="Public">Public</option></TextField>
      <FormControlLabel control={<Switch checked={profile.showInPalsLists} onChange={event => updateProfile({ showInPalsLists: event.target.checked })} />} label="Show me in Digipals lists" />
    </CardContent></Card>}
    <Card sx={{ maxWidth: 680 }}>
      <CardContent sx={{ display: 'grid', gap: 3 }}>
        <Box>
          <Typography variant="h6" gutterBottom>Library tile size</Typography>
          <Typography color="text.secondary" sx={{ mb: 1.5 }}>Choose how large photos appear in My Dex. This does not change Upload Photos, Indexing, or the Photo Viewer.</Typography>
          <Slider
            value={settings.libraryTileSize}
            min={72}
            max={280}
            step={8}
            valueLabelDisplay="auto"
            valueLabelFormat={value => `${value}px`}
            onChange={(_event, value) => update({ libraryTileSize: value })}
            aria-label="Library tile size"
            sx={{ maxWidth: 520, width: 'calc(100% - 32px)', mx: 2 }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', maxWidth: 520, width: 'calc(100% - 32px)', mx: 2, color: 'text.secondary' }}>
            <Typography variant="caption">More items</Typography>
            <Typography variant="caption">Larger images</Typography>
          </Box>
        </Box>
        <Box>
          <Typography variant="h6" gutterBottom>Item names</Typography>
          <FormControlLabel control={<Switch checked={settings.showItemNames} onChange={event => update({ showItemNames: event.target.checked })} />} label="Show item names under images" />
        </Box>
        <Box>
          <Typography variant="h6" gutterBottom>Animation</Typography>
          <Typography color="text.secondary" sx={{ mb: 1 }}>Skip the folder close/open movement when switching Dex views. The tab change remains animated and the next view loads immediately.</Typography>
          <FormControlLabel control={<Switch checked={settings.reduceAnimations} onChange={event => update({ reduceAnimations: event.target.checked })} />} label="Reduce animations" />
        </Box>
      </CardContent>
    </Card>
    <Card sx={{ maxWidth: 680, mt: 3 }}>
      <CardContent sx={{ display: 'grid', gap: 1 }}>
        <Typography variant="h6">Manage Storage</Typography>
        <Typography color="text.secondary">Completed items and photos waiting in Indexing share this storage allowance.</Typography>
        <StorageUsageBar usage={usage} />
      </CardContent>
    </Card>
    {adminUsers && <Card sx={{ maxWidth: 980, mt: 3 }}>
      <CardContent sx={{ display: 'grid', gap: 2 }}>
        <Box><Typography variant="h6">Admin Users</Typography><Typography color="text.secondary">Read-only account and storage overview.</Typography></Box>
        <Box component="form" onSubmit={searchAdminUsers} sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}><TextField size="small" label="Search users" value={adminSearch} onChange={event => setAdminSearch(event.target.value)} sx={{ flex: 1, minWidth: 220 }} /><Button type="submit" variant="contained" disabled={adminLoading} sx={{ backgroundColor: '#222', color: '#fff', fontWeight: 'bold', '&:hover': { backgroundColor: '#333' } }}>{adminLoading ? 'Searching…' : 'Search'}</Button></Box>
        <Box sx={{ display: 'grid', gap: 1 }}>{adminUsers.map(user => <Box key={user.id} sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.2fr 1.5fr 0.7fr 1.5fr' }, gap: 1, p: 1.5, border: '1px solid #ddd', borderRadius: 1 }}><Box><Typography fontWeight="bold">{user.username}</Typography><Typography variant="body2" color="text.secondary">{user.email}</Typography></Box><Typography variant="body2">Role: {user.role}</Typography><Typography variant="body2">Used: {formatBytes(user.usedBytes)}</Typography><Typography variant="body2">Completed {formatBytes(user.completedBytes)} · Pending {formatBytes(user.pendingBytes)}</Typography></Box>)}</Box>
        {adminUsers.length === 0 && <Typography color="text.secondary">No users found.</Typography>}
      </CardContent>
    </Card>}
    <Card sx={{ maxWidth: 680, mt: 3 }}>
      <CardContent component="form" onSubmit={changePassword} sx={{ display: 'grid', gap: 2 }}>
        <Box><Typography variant="h6">Change Password</Typography><Typography color="text.secondary">Use your current password to set a new one.</Typography></Box>
        {passwordMessage.text && <Alert severity={passwordMessage.type}>{passwordMessage.text}</Alert>}
        <TextField label="Current password" type="password" value={passwords.currentPassword} onChange={updatePassword('currentPassword')} required />
        <TextField label="New password" type="password" value={passwords.newPassword} onChange={updatePassword('newPassword')} required inputProps={{ minLength: 8 }} helperText="At least 8 characters" />
        <TextField label="Confirm new password" type="password" value={passwords.confirmPassword} onChange={updatePassword('confirmPassword')} required inputProps={{ minLength: 8 }} />
        <Button type="submit" variant="contained" disabled={changingPassword} sx={{ justifySelf: 'start', backgroundColor: '#222', color: '#fff', fontWeight: 'bold', '&:hover': { backgroundColor: '#333' } }}>{changingPassword ? 'Changing…' : 'Change Password'}</Button>
      </CardContent>
    </Card>
  </main>;
}

export default Settings;
